import { Box, Typography, Container, TextField, Button, Grid, Alert, CircularProgress, IconButton, InputAdornment, Paper } from '@mui/material';
import { motion, useInView } from 'framer-motion';
import { useState, useRef } from 'react';
import { Send, CheckCircle, ErrorOutlineOutlined, WhatsApp, Email, Phone } from '@mui/icons-material';

const ContactForm = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.subject.trim()) newErrors.subject = 'Subject is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    if (formData.message.length < 10) newErrors.message = 'Message must be at least 10 characters';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    // Clear error when user starts typing
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setSubmitError('');
    
    // Simulate API call (replace with actual backend integration)
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      console.log('Form submitted:', formData);
      setSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
      setTimeout(() => setSubmitted(false), 6000);
    } catch (error) {
      setSubmitError('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1, duration: 0.6 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
  };

  const quickContacts = [
    { icon: <Email />, label: 'Email', value: 'contact@johnasfar.com', link: 'mailto:contact@johnasfar.com' },
    { icon: <Phone />, label: 'Phone', value: '+27 (0) 11 123 4567', link: 'tel:+27111234567' },
    { icon: <WhatsApp />, label: 'WhatsApp', value: '+27 82 123 4567', link: 'https://wa.me/27821234567' },
  ];

  return (
    <Box
      ref={ref}
      sx={{
        py: { xs: 10, md: 18 },
        background: 'linear-gradient(135deg, #050505 0%, #0a0a0a 50%, #0f0f0f 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Container maxWidth="md">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={containerVariants}
        >
          {/* Header Section */}
          <motion.div variants={itemVariants}>
            <Typography
              variant="subtitle1"
              sx={{
                color: '#C8A96B',
                letterSpacing: 6,
                mb: 2,
                textAlign: 'center',
                fontSize: '0.85rem',
                fontWeight: 600,
                textTransform: 'uppercase',
              }}
            >
              SEND A MESSAGE
            </Typography>

            <Typography
              variant="h2"
              sx={{
                textAlign: 'center',
                mb: 3,
                fontSize: { xs: '2rem', sm: '2.8rem', md: '3.5rem' },
                fontWeight: 800,
                background: 'linear-gradient(135deg, #fff 0%, #e0e0e0 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Contact John Asfar Directly
            </Typography>

            <Typography
              sx={{
                textAlign: 'center',
                maxWidth: '650px',
                mx: 'auto',
                color: '#ccc',
                mb: 6,
                fontSize: '1.05rem',
                lineHeight: 1.6,
              }}
            >
              Have an idea, question, or proposal? Reach out directly and expect a 
              personal response within 3 business days.
            </Typography>
          </motion.div>

          {/* Success Alert */}
          {submitted && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              <Alert
                severity="success"
                icon={<CheckCircle />}
                sx={{
                  mb: 4,
                  background: 'rgba(200,169,107,0.1)',
                  color: '#C8A96B',
                  border: '1px solid rgba(200,169,107,0.3)',
                  '& .MuiAlert-icon': { color: '#C8A96B' },
                }}
              >
                <strong>Message sent successfully!</strong> John will respond within 3 business days.
              </Alert>
            </motion.div>
          )}

          {/* Error Alert */}
          {submitError && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Alert
                severity="error"
                icon={<ErrorOutlineOutlined />}
                sx={{
                  mb: 4,
                  background: 'rgba(211,47,47,0.1)',
                  color: '#ff6b6b',
                  border: '1px solid rgba(211,47,47,0.3)',
                }}
              >
                {submitError}
              </Alert>
            </motion.div>
          )}

          {/* Contact Form */}
          <motion.form variants={itemVariants} onSubmit={handleSubmit}>
            <Paper
              sx={{
                p: { xs: 3, md: 5 },
                background: 'linear-gradient(135deg, #0d0d0d 0%, #0a0a0a 100%)',
                border: '1px solid rgba(200,169,107,0.15)',
                borderRadius: '24px',
              }}
            >
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Your Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    error={!!errors.name}
                    helperText={errors.name}
                    disabled={loading}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        '& fieldset': { borderColor: 'rgba(200,169,107,0.3)' },
                        '&:hover fieldset': { borderColor: '#C8A96B' },
                        '&.Mui-focused fieldset': { borderColor: '#C8A96B' },
                      },
                      '& .MuiInputLabel-root': { color: '#999' },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#C8A96B' },
                      '& .MuiFormHelperText-root': { color: '#ff6b6b' },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Your Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    error={!!errors.email}
                    helperText={errors.email}
                    disabled={loading}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        '& fieldset': { borderColor: 'rgba(200,169,107,0.3)' },
                        '&:hover fieldset': { borderColor: '#C8A96B' },
                        '&.Mui-focused fieldset': { borderColor: '#C8A96B' },
                      },
                      '& .MuiInputLabel-root': { color: '#999' },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#C8A96B' },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    error={!!errors.subject}
                    helperText={errors.subject}
                    disabled={loading}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        '& fieldset': { borderColor: 'rgba(200,169,107,0.3)' },
                        '&:hover fieldset': { borderColor: '#C8A96B' },
                        '&.Mui-focused fieldset': { borderColor: '#C8A96B' },
                      },
                      '& .MuiInputLabel-root': { color: '#999' },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#C8A96B' },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Your Message"
                    name="message"
                    multiline
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    error={!!errors.message}
                    helperText={errors.message}
                    disabled={loading}
                    required
                    placeholder="Please provide details about your inquiry..."
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        '& fieldset': { borderColor: 'rgba(200,169,107,0.3)' },
                        '&:hover fieldset': { borderColor: '#C8A96B' },
                        '&.Mui-focused fieldset': { borderColor: '#C8A96B' },
                      },
                      '& .MuiInputLabel-root': { color: '#999' },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#C8A96B' },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading}
                    endIcon={!loading && <Send />}
                    sx={{
                      background: 'linear-gradient(135deg, #C8A96B, #b89750)',
                      color: '#000',
                      py: 1.8,
                      fontSize: '1.05rem',
                      fontWeight: 700,
                      borderRadius: '40px',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #D6B57B, #C8A96B)',
                        transform: 'translateY(-2px)',
                      },
                      '&.Mui-disabled': {
                        background: 'rgba(200,169,107,0.3)',
                        color: '#666',
                      },
                    }}
                  >
                    {loading ? <CircularProgress size={24} sx={{ color: '#000' }} /> : 'Send Message'}
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </motion.form>

          {/* Quick Contact Options */}
          <motion.div variants={itemVariants}>
            <Box sx={{ mt: 5, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: '#777', mb: 2 }}>
                Or connect through preferred channels
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
                {quickContacts.map((contact, idx) => (
                  <Button
                    key={idx}
                    href={contact.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={contact.icon}
                    sx={{
                      color: '#C8A96B',
                      borderColor: 'rgba(200,169,107,0.3)',
                      '&:hover': {
                        borderColor: '#C8A96B',
                        background: 'rgba(200,169,107,0.05)',
                      },
                    }}
                    variant="outlined"
                    size="small"
                  >
                    {contact.label}
                  </Button>
                ))}
              </Box>
            </Box>
          </motion.div>

          {/* Response Guarantee */}
          <motion.div variants={itemVariants}>
            <Box
              sx={{
                mt: 4,
                p: 2,
                borderRadius: '12px',
                background: 'rgba(200,169,107,0.03)',
                textAlign: 'center',
              }}
            >
              <Typography variant="caption" sx={{ color: '#666' }}>
                🔒 Your information is confidential • 📧 Response guaranteed within 72 hours • 🌍 Global inquiries welcome
              </Typography>
            </Box>
          </motion.div>
        </motion.div>
      </Container>
    </Box>
  );
};

export default ContactForm;